import { useNavigate } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Checkbox } from '@factorialco/f0-react'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { useHover } from 'modules/core/lib/hoverContext'

import type { Column } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/buildColumns'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionDispatch } from 'modules/shift_management/hooks/useSelection'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import View from 'modules/shift_management/store/internal/models/View'

import SortEmployeesOptions from './SortEmployeesOptions'
import { WorkAreasFirstCell } from './WorkAreasFirstCell'

export interface HeaderContentProps {
  heading: Column
  index: number
  isChecked: boolean
  onClick: () => void
  highlighted?: boolean
  canManageShifts: boolean
}

const HeaderContent = ({
  heading,
  index,
  isChecked,
  onClick,
  highlighted,
  canManageShifts,
}: HeaderContentProps) => {
  const [{ hasFeatureFlag, featureFlags }] = useEnabledFeatures()
  const { navigation } = useNavigation()

  const hover = useHover()
  const navigate = useNavigate()

  const [{ view }, dispatchSchedule] = useSchedule()
  const dispatchSelection = useSelectionDispatch()
  const [, dispatchPagination] = usePagination()
  const [, dispatchFilters] = useFilters()
  const [, dispatchShiftsRows] = useShiftsRows()
  const [, dispatchUI] = useUI()

  const { date } = heading
  const isWeeklyByEmployees = view.isWeekly && view.isByEmployees
  const isDailyViewEnabled = hasFeatureFlag('shift_management_daily_view')
  // First cell should not be redirectable
  const canRedirectToDaily = isWeeklyByEmployees && isDailyViewEnabled && index != 0

  const { isTimeFormat24H } = useIsTimeFormat24H()

  const showCheckbox = isChecked
  const isCheckboxEnabled = canManageShifts && !view.isDaily && (hover || showCheckbox)

  const redirectToSelectedDay = () => {
    const view = new View({
      date: date,
      viewType: 'daily',
      groupType: 'employees',
      isTimeFormat24H,
      featureFlags,
    })

    track(trackingEvents.shiftManagement.selectShiftManagementView, {
      view: view.viewType,
      view_type_by: view.groupType,
      from: 'table_days_header',
    })

    navigate(
      navigation.shifts.customViewGroup.fullPath({
        viewType: view.viewType,
        groupType: view.groupType,
        year: view.startRangeYear,
        month: view.startRangeMonth,
        day: view.startRangeDay,
      })
    )
    dispatchUI({ type: 'LOADING' })
    dispatchSchedule({ type: 'RESET_VIEW', view })
    dispatchSelection({ type: 'CLEAR_SELECTION' })
    dispatchFilters({ type: 'RESET_SHIFT_IDS' })
    dispatchShiftsRows({ type: 'RESET_SHIFTS_ROWS' })
    dispatchPagination({ type: 'RESET_PAGINATION' })
  }

  const redirectToDailyView = () => {
    if (!canRedirectToDaily) return

    return redirectToSelectedDay
  }

  return (
    <Box flexDirection="row" height="full" width="full" alignItems="center" gap="s4">
      <Box flexDirection="row" justifyContent={view.isDaily ? 'center' : 'flexStart'} width="full">
        {index == 0 && view.isByEmployees ? (
          <SortEmployeesOptions
            isCheckboxEnabled={isCheckboxEnabled}
            isChecked={isChecked}
            onClick={onClick}
          />
        ) : index == 0 && view.isByAreas ? (
          <WorkAreasFirstCell
            isCheckboxEnabled={isCheckboxEnabled}
            title={heading.headerCell}
            isChecked={isChecked}
            onClick={redirectToDailyView}
          />
        ) : (
          <Box flexDirection="row" width="full" alignItems="center">
            <Box
              flexDirection="row"
              justifyContent="flexStart"
              width="full"
              cursor={canRedirectToDaily ? 'pointer' : 'default'}
              onClick={isCheckboxEnabled ? onClick : redirectToDailyView()}
            >
              {highlighted ? (
                <Box flexDirection="row" alignItems="center" gap="s4">
                  <Box
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="center"
                    background="grey400"
                    borderRadius={{ all: 'abs004' }}
                    className="bg-f1-icon-accent"
                    height="s20"
                    width="s24"
                    style={{ userSelect: 'none' }}
                  >
                    <Text size="200" weight="bold" className="text-f1-foreground-inverse">
                      {heading.headerCell.match(/\d+/)?.[0]}
                    </Text>
                  </Box>
                  <Text
                    size="200"
                    weight="normal"
                    className={
                      !view.isDaily ? 'text-f1-foreground' : 'text-f1-foreground-secondary'
                    }
                  >
                    {heading.headerCell.replace(/\d+/, '').trim()}
                  </Text>
                </Box>
              ) : (
                <Box
                  flexDirection="row"
                  alignItems="center"
                  gap="s2"
                  style={{ userSelect: 'none' }}
                >
                  <Text size="200" weight="medium" className="text-f1-foreground">
                    {heading.headerCell.match(/\d+/)?.[0]}
                  </Text>
                  <Text size="200" weight="normal" className="text-f1-foreground-secondary">
                    {heading.headerCell.replace(/\d+/, '').trim()}
                  </Text>
                </Box>
              )}
            </Box>
            <Box
              width="min-content"
              flexDirection="row"
              data-testid="menu-order"
              justifyContent="flexEnd"
            >
              {isCheckboxEnabled && <F0Checkbox checked={isChecked} onCheckedChange={onClick} />}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default observer(HeaderContent)
